import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { Mascot } from "../brand/Mascot";
import { LOGOS } from "../../brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  email: "",
  company: "",
  details: "",
};

export const StartAProject = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.details) {
      toast.error("Name, email, and project details are required.");
      return;
    }
    setSubmitting(true);
    try {
      // Preserve existing SendGrid + MongoDB contact pipeline (backend expects `interest` field)
      await axios.post(`${API}/contact`, {
        ...form,
        interest: "Custom AI build",
      });
      toast.success("Sent. I'll get back to you personally.");
      setSent(true);
      setForm(initialState);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something broke on the way over. Try again in a sec.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="start-a-project"
      data-testid="start-a-project-section"
      className="relative overflow-hidden"
      style={{
        paddingTop: "clamp(64px, 10vh, 140px)",
        paddingBottom: "clamp(64px, 10vh, 140px)",
        background:
          "radial-gradient(1000px 500px at 30% 20%, rgba(11,60,220,0.14), transparent 70%), linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.15))",
      }}
    >
      {/* legacy anchor compatibility */}
      <span id="contact" className="block relative -top-24" aria-hidden />

      {/* Oversized approved Orbital G — derived-only, not redrawn */}
      <img
        aria-hidden
        src={LOGOS.orbitalG}
        alt=""
        loading="lazy"
        className="pointer-events-none absolute select-none"
        style={{
          right: "-160px",
          bottom: "-160px",
          width: "min(680px, 82vw)",
          opacity: 0.05,
          filter: "blur(1px)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl">
          <h2
            className="font-condensed font-bold text-white uppercase"
            style={{
              fontSize: "clamp(2.25rem, 5vw, 4rem)",
              lineHeight: 0.98,
              letterSpacing: "-0.025em",
            }}
            data-testid="sap-headline"
          >
            Got Something In Your Head?
          </h2>
          <p
            className="mt-4 font-condensed font-bold ggm-gradient-text uppercase"
            style={{
              fontSize: "clamp(1.4rem, 3vw, 2.25rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
            }}
            data-testid="sap-sub"
          >
            Let's turn it into something real.
          </p>
          <p className="mt-8 font-body text-white/75 text-base md:text-lg leading-relaxed">
            You don't need to know the tech stack.
          </p>
          <p className="mt-2 font-body text-white/75 text-base md:text-lg leading-relaxed">
            Just tell me what you want to happen when the work is done.
          </p>
          <p
            className="mt-4 font-condensed font-bold text-white uppercase"
            style={{
              fontSize: "clamp(1.15rem, 2.3vw, 1.5rem)",
              letterSpacing: "0.01em",
              lineHeight: 1.2,
            }}
          >
            Just tell me what you're trying to accomplish.
          </p>

          {/* Free 30-min offer callout */}
          <div
            className="mt-6 rounded-xl p-5"
            style={{
              background:
                "linear-gradient(135deg, rgba(11,60,220,0.18), rgba(40,200,240,0.08))",
              border: "1px solid rgba(40,200,240,0.35)",
            }}
            data-testid="sap-free-audit-callout"
          >
            <p className="font-condensed uppercase tracking-widest text-[11px] text-[var(--ggm-signal-cyan)] font-bold mb-2">
              Free 30 minutes to start · one or the other
            </p>
            <ul className="font-body text-white/85 text-sm md:text-base leading-relaxed space-y-1.5">
              <li>
                <span className="text-white font-bold">Virtual scoping call</span>{" "}
                — jump on Zoom and we figure out if there's a build worth doing.
              </li>
              <li>
                <span className="text-white font-bold">Local AI Audit at your business</span>{" "}
                — I'll come to you (within 15 miles) and walk your workflow in person to spot AI wins.
              </li>
            </ul>
            <p className="mt-3 font-condensed uppercase tracking-widest text-[10px] text-white/55">
              No obligation. Pick one in the project details below.
            </p>
          </div>

          <div
            className="mt-6 rounded-xl p-5"
            style={{
              background: "rgba(11,30,58,0.4)",
              border: "1px solid var(--ggm-border-subtle)",
            }}
            data-testid="sap-examples"
          >
            <p className="font-condensed uppercase tracking-widest text-[11px] text-[var(--ggm-signal-cyan)] mb-3">
              Examples — if you're not sure where to start
            </p>
            <ul className="font-body text-white/80 text-sm md:text-base leading-relaxed space-y-1.5">
              <li>• Follow up with leads automatically and book more calls</li>
              <li>• Turn messy inbox + requests into organized tasks</li>
              <li>• Generate weekly reports without touching a spreadsheet</li>
              <li>• Build an internal tool your team uses every day</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_auto] items-start">
          <form
            onSubmit={submit}
            noValidate
            className="rounded-3xl p-6 md:p-8"
            style={{
              background: "rgba(11,30,58,0.55)",
              border: "1px solid var(--ggm-border-subtle)",
              boxShadow: "var(--ggm-shadow-md)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
            }}
            data-testid="sap-form"
            aria-labelledby="sap-form-prompt"
          >
            <p
              id="sap-form-prompt"
              className="font-condensed font-bold text-white uppercase mb-2"
              style={{ fontSize: "clamp(1.05rem, 2vw, 1.3rem)", letterSpacing: "0.02em" }}
            >
              What workflow do you want off your plate?
            </p>
            <p className="font-body text-white/55 text-xs leading-relaxed mb-6 max-w-xl">
              <span className="text-white/80">
                For builds booked by Dec 31, I'll include 12 months of hosting + basic
                maintenance (a $75/mo value), then $75/mo after year one.
              </span>{" "}
              Domains, third-party tools, and usage-based AI/API costs are separate.
            </p>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="ggm-eyebrow mb-2 block">Name <span aria-hidden style={{ color: "var(--ggm-signal-cyan)" }}>*</span></span>
                <input
                  type="text"
                  required
                  className="ggm-input"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="sap-input-name"
                  autoComplete="name"
                />
              </label>
              <label className="block">
                <span className="ggm-eyebrow mb-2 block">Email <span aria-hidden style={{ color: "var(--ggm-signal-cyan)" }}>*</span></span>
                <input
                  type="email"
                  required
                  className="ggm-input"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="sap-input-email"
                  autoComplete="email"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="ggm-eyebrow mb-2 block">Company</span>
                <input
                  type="text"
                  className="ggm-input"
                  value={form.company}
                  onChange={update("company")}
                  data-testid="sap-input-company"
                  autoComplete="organization"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="ggm-eyebrow mb-2 block">
                  What's the workflow + what tools do you use today?{" "}
                  <span aria-hidden style={{ color: "var(--ggm-signal-cyan)" }}>*</span>
                </span>
                <textarea
                  required
                  rows={5}
                  className="ggm-input"
                  value={form.details}
                  onChange={update("details")}
                  data-testid="sap-input-details"
                  placeholder={'Example: "Website form → Gmail → manual follow-up → scheduling."\n\nTell me what happens today, what you want to happen instead, and what tools you use (Gmail, Sheets, HubSpot, Calendar, etc.).'}
                />
              </label>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                disabled={submitting}
                className="ggm-btn-primary text-base md:text-lg px-6 py-4 disabled:opacity-70"
                data-testid="sap-submit"
              >
                {sent ? (
                  <>
                    Sent <CheckCircle2 size={18} />
                  </>
                ) : (
                  <>
                    Get 3 automation ideas <Send size={18} />
                  </>
                )}
              </button>
              {sent && (
                <span
                  className="font-condensed uppercase tracking-widest text-[12px]"
                  style={{ color: "var(--ggm-signal-cyan)" }}
                  data-testid="sap-sent-inline"
                  role="status"
                  aria-live="polite"
                >
                  Message received — I'll reply personally.
                </span>
              )}
            </div>

            <p className="mt-6 font-condensed uppercase tracking-widest text-[11px] text-white/50">
              No sales team. No automated pitch. You'll hear from me.
            </p>
            <p
              className="mt-2 font-condensed uppercase tracking-widest text-[11px] text-white/50"
              data-testid="sap-reply-sla"
            >
              I usually reply within 1 business day.
            </p>
          </form>

          {/* Welcoming mascot — transparent artwork, no frame/mask needed */}
          <div
            className="hidden lg:block shrink-0 self-end"
            style={{ width: 200 }}
            data-testid="sap-mascot-frame"
          >
            <Mascot pose="welcoming" size="lg" className="mx-auto" style={{ height: 260, width: "auto" }} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default StartAProject;
